/* eslint-disable import/no-relative-packages */
/* eslint-disable no-param-reassign */
import { parse as cookiesParse } from 'cookie';
import qs from 'querystring';
/**
 * Import directly like this because these exports are not
 * exposed in the `exports` field in package.json
 */
import { UserJsonldUserReadUserPrivateRead } from '@/api/generated';
import { USER_ROLE_API_REF } from '@/types/modifiers/references';
import { IncomingMessage, ServerResponse } from 'http';
import type { Session } from 'next-auth';
import { env } from 'next-runtime-env';

export const sessionRefetchIntervalInSeconds = 60;

const FINISH_REGISTRATION_ROUTE = '/register';
const FINISH_REGISTRATION_ROUTE_SHOP = '/register-shop';
const REGISTRATION_SHOP_IN_PROGRESS = '/register-shop/register-shop-confirmation';

const PUBLIC_ACCESS_ROUTES_SIGNUP = ['/privacy-policy', '/website-terms'];

export const serializeSession = (session: Session) => {
  if (session?.user && session?.user.name === undefined) {
    session.user.name = null;
  }

  if (session?.user && session.user.email === undefined) {
    session.user.email = null;
  }

  if (session?.user && session.user.image === undefined) {
    session.user.image = null;
  }

  return session;
};

export async function getServerSignInUrl(
  req: IncomingMessage,
  res: ServerResponse<IncomingMessage>,
  locale?: string,
  isMobileApp?: boolean,
  callbackUrl?: URL,
) {
  const csfrTokenResponse = await fetch(`${env('NEXTAUTH_URL')}/api/auth/csrf`);

  const cookieStore = cookiesParse(csfrTokenResponse.headers.getSetCookie().join('; ') ?? '');

  const { csrfToken } = await csfrTokenResponse.json();

  if (csrfToken) {
    cookieStore.csrfToken = csrfToken;
  }

  const authorizationParams = new URLSearchParams();

  if (isMobileApp) {
    authorizationParams.append('redirect_uri', 'altered://auth/redirect');
    authorizationParams.append('shop_redirect_uri', 'altered://auth/redirect-shop');
  } else {
    authorizationParams.append(
      'shop_redirect_uri',
      new URL(`/api/auth/callback/keycloak-shop`, env('NEXTAUTH_URL')).toString(),
    );
  }

  if (locale) {
    authorizationParams.append('ui_locales', locale);
  }

  const response = await fetch(
    `${env('NEXTAUTH_URL')}/api/auth/signin/${isMobileApp ? 'keycloak-mobile' : 'keycloak'}?${authorizationParams}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Cookie: Object.entries(cookieStore)
          .map(([key, value]) => `${key}=${value}`)
          .join('; '),
      },
      body: qs.stringify({
        callbackUrl: callbackUrl?.toString() ?? req.url,
        redirect: false,
        json: true,
        csrfToken,
      }),
    },
  );

  const data = await response.json();

  res.setHeader('Set-Cookie', response.headers.getSetCookie() ?? '');

  return {
    redirect: data.url,
  };
}

export async function shouldRedirectRegister(
  user: UserJsonldUserReadUserPrivateRead,
  pathname: string,
): Promise<string | false> {
  if (
    user &&
    !user.isActive &&
    !PUBLIC_ACCESS_ROUTES_SIGNUP.find((path) => pathname.includes(path)) &&
    ![REGISTRATION_SHOP_IN_PROGRESS, FINISH_REGISTRATION_ROUTE_SHOP, FINISH_REGISTRATION_ROUTE].find((path) =>
      pathname.includes(path),
    )
  ) {
    if (user.roles?.includes(USER_ROLE_API_REF.ROLE_SHOP_OWNER)) {
      if (user.shop) {
        return REGISTRATION_SHOP_IN_PROGRESS;
      }

      return FINISH_REGISTRATION_ROUTE_SHOP;
    }

    return FINISH_REGISTRATION_ROUTE;
  }

  return false;
}
